// ==========================
// Version 3 — pages/AdminUsers.js
// - Paging (limit/startAfter by doc id)
// - Search (client-side on current page)
// - Quick inspector modal (loads full user doc)
// - Admin toggle (writes roles.admin; logs via logAdminActivity with real actor uid)
// ==========================
import React from 'react';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { logAdminActivity } from '../db';
import {
  collection,
  getDocs,
  orderBy,
  query,
  limit,
  startAfter,
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';

const PAGE_SIZE = 20;

export default function AdminUsers() {
  const { currentUser } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [qtxt, setQtxt] = React.useState('');

  // pagination state
  const [page, setPage] = React.useState(0);            // current page index (0-based)
  const [hasNext, setHasNext] = React.useState(false);  // if there is another page
  const cursorsRef = React.useRef([]);                  // array<QueryDocumentSnapshot> of last docs per page

  // inspector modal
  const [inspecting, setInspecting] = React.useState({ open: false, uid: null, loading: false, data: null });

  // Load a page. For page 0, no cursor; for page n>0, startAfter(cursors[n-1])
  const loadPage = React.useCallback(async (pageIndex) => {
    setLoading(true);
    try {
      const col = collection(db, 'users');
      const base = query(col, orderBy('__name__'), limit(PAGE_SIZE + 1)); // +1 to detect next page
      const cursor = pageIndex > 0 ? cursorsRef.current[pageIndex - 1] : null;
      const q = cursor ? query(col, orderBy('__name__'), startAfter(cursor), limit(PAGE_SIZE + 1)) : base;

      const snap = await getDocs(q);
      const docs = snap.docs;
      const pageRows = docs.slice(0, PAGE_SIZE).map((d) => {
        const data = d.data() || {};
        return {
          uid: d.id,
          displayName: data?.profile?.displayName || '',
          admin: !!data?.roles?.admin,
          hasPrefs: !!data?.prefs,
        };
      });

      // keep/adjust cursors
      const lastForThisPage = docs.length > 0 ? docs[Math.min(docs.length - 1, PAGE_SIZE - 1)] : null;
      // store/replace cursor at index
      if (lastForThisPage) {
        const next = [...cursorsRef.current];
        next[pageIndex] = lastForThisPage;
        cursorsRef.current = next;
      }

      setRows(pageRows);
      setHasNext(docs.length > PAGE_SIZE);
      setPage(pageIndex);
    } finally {
      setLoading(false);
    }
  }, []);

  // initial load
  React.useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // navigation handlers
  const goNext = () => {
    if (!hasNext) return;
    loadPage(page + 1);
  };
  const goPrev = () => {
    if (page === 0) return;
    // For previous page, we need to query starting after the cursor of (targetIndex - 1)
    loadPage(page - 1);
  };
  const refresh = () => loadPage(page);
  const goFirst = () => loadPage(0);

  // search filter (client-side on the current page)
  const filtered = rows.filter((r) => {
    const t = qtxt.trim().toLowerCase();
    if (!t) return true;
    return r.uid.toLowerCase().includes(t) || (r.displayName || '').toLowerCase().includes(t);
  });

  // Open inspector
  const openInspector = async (uid) => {
    setInspecting({ open: true, uid, loading: true, data: null });
    try {
      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      setInspecting({
        open: true,
        uid,
        loading: false,
        data: snap.exists() ? snap.data() : null,
      });
    } catch (e) {
      setInspecting({ open: true, uid, loading: false, data: { error: e?.message || String(e) } });
    }
  };
  const closeInspector = () => setInspecting({ open: false, uid: null, loading: false, data: null });

  // Toggle admin role
  const toggleAdmin = async (uid, current) => {
    const newVal = !current;
    // optimistic UI
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, admin: newVal } : r)));
    try {
      // 1) update role
      await setDoc(doc(db, 'users', uid), { roles: { admin: newVal } }, { merge: true });

      // 2) write activity log
      await logAdminActivity({
        action: 'role_change',
        actorUid: currentUser?.uid || null,
        targetUid: uid,
        details: { field: 'roles.admin', value: newVal },
      });
    } catch (e) {
      // revert on failure
      setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, admin: current } : r)));
      const code = e?.code || '';
      if (code === 'permission-denied') {
        alert(
          'Permission denied updating roles/admin or writing admin_activity.\n' +
          'Check Firestore rules.'
        );
      } else {
        alert(`Failed to update role: ${e?.message || e}`);
      }
    }
  };

  return (
    <>
      <div style={headerRow}>
        <h1 style={h1}>Users</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={goFirst} style={btnMinor} disabled={loading || page === 0}>First</button>
          <button type="button" onClick={goPrev} style={btnMinor} disabled={loading || page === 0}>Prev</button>
          <button type="button" onClick={goNext} style={btnMinor} disabled={loading || !hasNext}>Next</button>
          <button type="button" onClick={refresh} style={btnMinor} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          placeholder="Search by uid or name… (current page)"
          value={qtxt}
          onChange={(e) => setQtxt(e.target.value)}
          style={input}
        />
        <span style={{ color: WARM_GRAY, fontSize: 12 }}>
          Page {page + 1} • Showing {filtered.length}/{rows.length}
        </span>
      </div>

      <div style={table}>
        <div style={thead}>
          <div>UID</div>
          <div>Name</div>
          <div>Admin</div>
          <div>Prefs?</div>
          <div>Actions</div>
        </div>

        {loading ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>No users found on this page.</div>
        ) : (
          filtered.map((r) => (
            <div key={r.uid} style={trow}>
              <code
                style={{ fontSize: 12, cursor: 'pointer' }}
                onClick={() => openInspector(r.uid)}
                title="Click to inspect"
              >
                {r.uid}
              </code>
              <div title={r.displayName || '—'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.displayName || '—'}
              </div>
              <div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={r.admin}
                    onChange={() => toggleAdmin(r.uid, r.admin)}
                    title="Toggle admin"
                  />
                  {r.admin ? 'Yes' : 'No'}
                </label>
              </div>
              <div>{r.hasPrefs ? 'Yes' : 'No'}</div>
              <div>
                <button type="button" style={btnTiny} onClick={() => openInspector(r.uid)}>
                  Inspect
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* inspector modal */}
      {inspecting.open && (
        <div style={modalBackdrop} onClick={closeInspector} role="dialog" aria-modal="true">
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ margin: 0, fontFamily: 'serif', fontSize: 20 }}>User</h2>
              <button type="button" onClick={closeInspector} style={btnTiny}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: WARM_GRAY, marginBottom: 8 }}>
              <strong>uid:</strong> <code>{inspecting.uid}</code>
            </div>
            {inspecting.loading ? (
              <div style={{ color: WARM_GRAY }}>Loading…</div>
            ) : (
              <pre style={pre}>
                {JSON.stringify(inspecting.data ?? { error: 'Not found' }, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* --- styles --- */
const h1 = { margin: '0 0 12px', fontFamily: 'serif', fontSize: 26, color: IVORY };
const headerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 };

const input = {
  flex: 1,
  padding: 10,
  borderRadius: 10,
  border: '1px solid rgba(253,252,249,0.2)',
  background: '#fff',
};

const table = { display: 'grid', gap: 6 };
const gridCols = '1.6fr 1fr 110px 90px 120px';

const thead = {
  display: 'grid',
  gridTemplateColumns: gridCols,
  gap: 8,
  fontWeight: 800,
  color: IVORY,
  background: 'rgba(253,252,249,0.06)',
  padding: 10,
  borderRadius: 10,
};

const trow = {
  display: 'grid',
  gridTemplateColumns: gridCols,
  gap: 8,
  background: '#fff',
  color: NAVY,
  padding: 10,
  borderRadius: 10,
  alignItems: 'center',
};

const btnMinor = {
  appearance: 'none',
  background: 'transparent',
  color: IVORY,
  border: `1px solid ${GOLD}`,
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 700,
};

const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 999,
};

const modalCard = {
  width: 'min(720px, 92vw)',
  maxHeight: '80vh',
  overflow: 'auto',
  background: IVORY,
  color: NAVY,
  borderRadius: 14,
  border: '1px solid rgba(201,164,76,0.35)',
  boxShadow: '0 16px 48px rgba(10,26,51,0.35)',
  padding: 12,
};

const pre = {
  margin: 0,
  padding: 12,
  borderRadius: 10,
  background: '#fff',
  border: '1px solid rgba(10,26,51,0.12)',
  overflow: 'auto',
};
// ==========================
// End Version 3 — pages/AdminUsers.js
// ==========================
